import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { TooltipDirective } from '@cloudfactorydk/ng2-tooltip-directive';
import { ModalController, Platform } from '@ionic/angular';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { injectIpInfo } from 'src/app/shared-lib/core/utils';
import { ProductService, ProductSnapshot, ProductType } from 'src/app/shared-lib/domains/product';
import { environment } from 'src/environments/environment';
import { OrdersBlockedAlertComponent } from "../components/orders-blocked-alert/orders-blocked-alert.component";
import { AuthService } from '../core/auth.service';
import { ShoppingCart } from '../models/cart.model';
import { BankInformationSettings, BannersSettings, Finishing, GeneralSettings, LaminationType, LaminationWeight, PageOrientation, PageSize, PagesPerSheet, PageTurn, PaperWeight, PrintColor, PrintSettingsConfig, SettingCategory, Settings } from '../shared-lib/core/settings';
import { FeeType } from '../shared-lib/global/models';
import { UserService } from './../shared-lib/domains/user';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private productService = inject(ProductService);
  private isInit = new BehaviorSubject<boolean>(false);
  public isInit$ = this.isInit.asObservable();

  public options = environment.options;
  private readonly _ipInfo = injectIpInfo();
  private readonly _generalSettings = new BehaviorSubject<Partial<GeneralSettings>>({
    homeBanner: '',
    fees: {
      share: 0,
      referrer: 0,
      coupon: 0,
      creator: 0,
      royalty: 0,
    },
    influencerText: '',
    minVersion: null,
    inMaintenance: false,
    ordersBlockedTitle: '',
    ordersBlockedBody: ''
  });
  ordersBlockedTitle: '';
  ordersBlockedBody: '';
  isOrdersBlocked = false;
  readonly generalSettings$ = this._generalSettings.asObservable();

  private userService: UserService = inject(UserService);

  public isLogged$ = this.userService.user$.pipe(map(user => {
    return user && !user.isAnonymous;
  }));

  public isntAceptanceAmbassador$ = this.userService.user$.pipe(map(user => {
    return user && !user?.acceptance?.referrer;
  }));

  isMobile = signal<boolean>(false);

  /** True only on actual mobile phones (excludes tablets, TVs, etc.) */
  readonly isMobileDevice: boolean = (() => {
    const ua = navigator.userAgent;
    return /Mobi|iPhone/i.test(ua) && !/Tablet|iPad|TV|SmartTV|BRAVIA/i.test(ua);
  })();

  whatsappPhoneNumber = signal<string | null>(null);

  appBanners: BannersSettings;

  fee = {
    userId: null as string,
    type: null as FeeType
  };
  referrerId: string = null;
  bankInformation: BankInformationSettings;
  cur = 'EUR';
  taxPct = 21;

  _ringColors = [];
  // _ringColors = [
  //   { name: 'Transparente', color: '#FFFFFF', id: 'transparent' /*, default: true */ },
  //   { name: 'Negro',        color: '#000000', id: 'black'       },
  //   { name: 'Turquesa',     color: '#40E0D0', id: 'turquoise'   },
  //   { name: 'Rosa Pastel',  color: '#FFC0CB', id: 'light-pink'  },
  //   { name: 'Lila',         color: '#9370DB', id: 'lilac'       },
  //   { name: 'Azul Pastel',  color: '#87CEFA', id: 'light-blue'  },
  //   { name: 'Naranja',      color: '#FFA500', id: 'orange'      },
  //   { name: 'Amarilla',     color: '#FFFF00', id: 'yellow'      },
  //   { name: 'Verde Pastel', color: '#BDECB6', id: 'light-green' },
  //   { name: 'Rojo',         color: '#FF6961', id: 'red'         },
  // ]

  // _hardCovers = [
  //   { name:'Transparente',            color: '#FFFFFF', id: 'transparent' /*, defaultFront: true */ },
  //   { name:'Negro (Opaca)',           color: '#000000', id: 'black' /*,       defaultBack: true */ },
  //   { name:'Turquesa',                color: '#40E0D0', id: 'turquoise'   },
  //   { name:'Rosa Pastel (Opaca)',     color: '#FFC0CB', id: 'light-pink'  },
  //   { name:'Lila Pastel (Opaca)',     color: '#9370DB', id: 'lilac'       },
  //   { name:'Azul Pastel (Opaca)',     color: '#87CEFA', id: 'light-blue'  },
  //   { name:'Naranja Pastel (Opaca)',  color: '#FFA500', id: 'orange'      },
  //   { name:'Amarillo Pastel (Opaca)', color: '#FFFF00', id: 'yellow'      },
  //   // { name:'Verde Pastel (Opaca)',    color: '#BDECB6', id: 'light-green' },
  //   // no red
  // ]
  private printSettingsLoaded = new BehaviorSubject<boolean>(false);
  public printSettingsLoaded$ = this.printSettingsLoaded.asObservable();

  // Emite `true` una vez se han poblado las opciones de ringColor/hardCoverFront/hardCoverBack
  // a partir del producto de impresión (Firestore). Permite a los consumidores refrescar su
  // foto de `printSettings` (que `getPrintSettings()` devuelve por valor, no por referencia)
  // cuando la carga llega tarde y evita que el bloque de Encuadernado quede vacío hasta recargar.
  private _ringsCoversLoaded = new BehaviorSubject<boolean>(false);
  public ringsCoversLoaded$ = this._ringsCoversLoaded.asObservable();

  private printSettings = null;
  private _printSettings = { // OJO las opciones se manejan (index+1). selected =0 (no hay ningun seleccionado)
    skipFitToPage: {
      title: 'Es una programación didáctica',
      selected: 2,
      options: [
        {
          title: 'Sí',
          subTitle: null,
          // subTitle:'290 x 270 mm',
          size: '6',
          value: true,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [1, 3],
              disabledTooltip: 'Tamaño de papel incompatible'
            },
            {
              id: 'pagesPerSheet',
              options: [2, 3],
              disabledTooltip: 'Sólo a 1 pág. por cara'
            },
          ]
        },
        {
          title: 'No',
          subTitle: null,
          // subTitle:'290 x 270 mm',
          size: '6',
          value: false
        }
      ]
    },
    pageSize: {
      title: 'Tamaño del papel',
      selected: 2,
      options: [
        {
          title: 'A5',
          subTitle: '14.8x21cm',
          // subTitle:'290 x 270 mm',
          size: '4',
          value: PageSize.A5,
          unavailableFor: [
            {
              id: 'skipFitToPage',
              options: [1],
              disabledTooltip: 'Desact. Programación'
            },
            {
              id: 'finishing',
              options: [2, 6, 7],
              disabledTooltip: 'Acabado incompatible'
            },
            {
              id: 'coverLaminated',
              options: [2],
              disabledTooltip: 'Desact. Portada plastificada'
            },
            {
              id: 'paperWeight',
              options: [4],
              disabledTooltip: 'Agotado'
            }
          ]
        },
        {
          title: 'A4',
          subTitle: '21x29.7cm',
          // subTitle:'290 x 270 mm',
          size: '4',
          value: PageSize.A4,
          unavailableFor: [
            {
              id: 'paperWeight',
              options: [4],
              disabledTooltip: 'Agotado'
            }
          ]
        },
        {
          title: 'A3',
          subTitle: '29.7x42cm',
          // subTitle:'420 x 297 mm',
          size: '4',
          value: PageSize.A3,
          unavailableFor: [
            {
              id: 'skipFitToPage',
              options: [1],
              disabledTooltip: 'Desact. Programación'
            },
            {
              id: 'finishing',
              options: [2, 3, 4, 5, 6, 7],
              disabledTooltip: 'Acabado incompatible'
            },
            {
              id: 'paperWeight',
              options: [5],
              disabledTooltip: 'Agotado'
            },
            {
              id: 'twoSided',
              options: [2],
              disabledTooltip: 'Solo disponible para impresión a una cara'
            }
          ]
        }
      ]
    },
    paperWeight: {
      title: 'Grosor del papel',
      selected: 1,
      options: [
        {
          title: '80 gr',
          subTitle: 'Un folio de toda la vida',
          size: '4',
          sizeMobile: '6',
          value: PaperWeight.W80,
        },
        {
          title: '90 gr',
          subTitle: 'Un poco más grueso',
          size: '4',
          sizeMobile: '6',
          value: PaperWeight.W90,
          unavailableFor: [
            {
              id: 'finishing',
              options: [5, 8],
              disabledTooltip: 'Acabado incompatible'
            }
          ],
          // hideSubtitleFor:[
          //   {
          //     id:"pageSize",
          //     options:[1]
          //   }
          // ]
        },
        {
          title: '100 gr',
          subTitle: 'Un poco más grueso aún',
          size: '4',
          sizeMobile: '6',
          value: PaperWeight.W100,
          unavailableFor: [
            {
              id: 'finishing',
              options: [5, 8],
              disabledTooltip: 'Acabado incompatible'
            }
          ],
          // hideSubtitleFor:[
          //   {
          //     id:"pageSize",
          //     options:[1]
          //   }
          // ]
        },
        {
          title: '120 gr',
          subTitle: 'Aún más grueso',
          size: '6',
          sizeMobile: '6',
          value: PaperWeight.W120,
          unavailableFor: [
            {
              id: "pageSize",
              options: [3],
              disabledTooltip: 'Solo disponible para los tamaños A5 y A4.'
            },
            {
              id: 'pageSize',
              options: [1, 2],
              disabledTooltip: 'Agotado'
            },
            {
              id: 'finishing',
              options: [5, 8],
              disabledTooltip: 'Acabado incompatible'
            }
          ],
        },
        {
          title: '160 gr',
          subTitle: 'Ni fino ni grueso',
          size: '6',
          sizeMobile: '6',
          value: PaperWeight.W160,
          unavailableFor: [
            {
              id: 'finishing',
              options: [5, 8],
              disabledTooltip: 'Acabado incompatible'
            },
            {
              id: "pageSize",
              options: [3],
              disabledTooltip: 'Agotado'
            },
          ],
          unavailableForGrouped: [
            {
              properties: [
                {
                  id: 'pageSize',
                  options: [1]
                },
                {
                  id: 'twoSided',
                  options: [2],
                }
              ],
              disabledTooltip: 'Solo disponible para impresión a una cara'
            }
          ]
          // hideSubtitleFor:[
          //   {
          //     id:"pageSize",
          //     options:[1]
          //   }
          // ]
        },
        {
          title: '200 gr',
          subTitle: 'Tipo cartulina fina',
          size: '6',
          sizeMobile: '6',
          value: PaperWeight.W200,
          unavailableFor: [
            {
              id: "pageSize",
              options: [1, 2],
              disabledTooltip: 'Solo disponible para el tamaño A3 e impreso a una cara'
            },
            {
              id: 'finishing',
              options: [5, 8],
              disabledTooltip: 'Acabado incompatible'
            }
          ],
          unavailableForGrouped: [
            {
              properties: [
                {
                  id: 'pageSize',
                  options: [3]
                },
                {
                  id: 'twoSided',
                  options: [2],
                }
              ],
              disabledTooltip: 'Solo disponible para impresión a una cara'
            }
          ]
          // hideSubtitleFor:[
          //   {
          //     id:"pageSize",
          //     options:[1]
          //   }
          // ]
        },
        {
          title: '300 gr',
          subTitle: 'Tipo cartulina del cole',
          size: '6',
          sizeMobile: '13',
          value: PaperWeight.W300,
          unavailableFor: [
            {
              id: 'finishing',
              options: [2, 3, 4, 5, 6, 7, 8],
              disabledTooltip: 'Acabado incompatible'
            },
            {
              id: 'twoSided',
              options: [2],
              // subTitle: 'Sólo a 1 cara'
              disabledTooltip: 'Solo disponible para impresión a una cara'
            },
          ]
        }
      ]
    },
    color: {
      title: 'Color de impresión',
      selected: 1,
      options: [
        {
          title: 'Blanco y negro',
          subTitle: 'Escala de grises',
          // title:'BYN',
          // subTitle:'Blanco y Negro',
          size: '6',
          value: PrintColor.BLACKNWHITE
        },
        {
          title: 'Color',
          subTitle: "Formato CMYK",
          // subTitle:'Colores CMYK',
          size: '6',
          value: PrintColor.COLOR
        }
      ]
    },
    // grosor:{
    //   title:"Grosor del Papel",
    //   selected:1,
    //   options:[
    //     {
    //       title:"80GR.",
    //       subTitle:"Papel Copistería",
    //       size:"4"
    //     },
    //     {
    //       title:"160GR.",
    //       subTitle:"Grueso Alto",
    //       size:"4",
    //       unavailableFor:[
    //         {
    //           id:"tamano",
    //           options:[2,3]
    //         }
    //       ]
    //     },
    //     {
    //       title:"280GR.",
    //       subTitle:"Tipo Cartulina",
    //       size:"4",
    //       unavailableFor:[
    //         {
    //           id:"tamano",
    //           options:[2,3]
    //         }
    //       ]
    //     },
    //   ]
    // },
    twoSided: {
      title: 'Forma de impresión',
      selected: 2,
      key: 'twoSided',
      options: [
        {
          title: 'A una cara',
          subTitle: 'Impreso por una cara del papel',
          size: '6',
          value: false
        },
        {
          title: 'A doble cara',
          subTitle: 'Impreso por delante y por detrás del folio',
          size: '6',
          value: true,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [3],
              disabledTooltip: 'No disponible para A3'
            },
          ]
        },
      ],
    },
    autoRotate: {
      title: 'Rotación automática',
      selected: 1,
      key: 'autoRotate',
      options: [
        {
          title: 'AutoRotación',
          subTitle: 'Automática',
          size: '6',
          value: true
        },
        {
          title: 'Sin AutoRotación',
          subTitle: 'Automática',
          size: '6',
          value: false
        },
      ]
    },
    pagesPerSheet: {
      title: 'Páginas por cara',
      selected: 1,
      options: [
        {
          title: '1 Página por cara',
          subTitle: null,
          size: '12',
          image: 'assets/icon/1pagePerSheet.svg',
          value: PagesPerSheet.ONE
        },
        {
          title: '2 Páginas por cara',
          subTitle: null,
          size: '12',
          image: 'assets/icon/2pagePerSheet.svg',
          value: PagesPerSheet.TWO,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [1]
            },
            {
              id: 'skipFitToPage',
              options: [1],
              disabledTooltip: 'Desact. Programación'
            }
          ]
        },
        {
          title: '4 Páginas por cara',
          subTitle: null,
          size: '12',
          image: 'assets/icon/4pagePerSheet.svg',
          value: PagesPerSheet.FOUR,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [1]
            },
            {
              id: 'skipFitToPage',
              options: [1],
              disabledTooltip: 'Desact. Programación'
            }
          ]
        },
      ]
    },
    pageOrientation: {
      title: 'Orientación del folio',
      selected: 1,
      options: [
        {
          title: 'Vertical',
          subTitle: null,
          size: '6',
          value: PageOrientation.PORTRAIT
        },
        {
          title: 'Horizontal',
          subTitle: null,
          size: '6',
          value: PageOrientation.LANDSCAPE
        },
      ]
    },
    pageTurn: {
      title: '¿Cómo quieres pasar las páginas?',
      selected: 1,
      tip: 'InfoPasarPaginaComponent',
      options: [
        {
          title: 'Borde largo',
          subTitle: null,
          size: '6',
          lottie: 'assets/lotties/vertical-largo.json',
          value: PageTurn.LARGESIDE
        },
        {
          title: 'Borde corto',
          subTitle: null,
          size: '6',
          lottie: 'assets/lotties/vertical-corto.json',
          value: PageTurn.SHORTSIDE,
          unavailableFor: [
            {
              id: 'finishing',
              options: [6, 7],
              disabledTooltip: 'Incompatible con perforado'
            }
          ]
        },
      ]
    },
    finishing: {
      title: null,
      selected: 1,
      categorized: true,
      options: [
        {
          title: 'Sin acabado',
          subTitle: 'Hojas sueltas',
          size: '6',
          sizeMobile: '13',
          image: 'assets/images/finishings/sinAcabado.svg',
          value: Finishing.NONE,
          settingCategory: SettingCategory.BASIC
        },
        {
          title: 'Grapado',
          subTitle: '50 folios máx.',
          size: '6',
          sizeMobile: '13',
          image: 'assets/images/finishings/grapado.svg',
          value: Finishing.STAPLED,
          settingCategory: SettingCategory.BASIC,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [1, 3],
              disabledTooltip: 'Tamaño de papel incompatible'
            },
            {
              id: 'paperWeight',
              options: [7],
              disabledTooltip: 'Gramaje incompatible'
            }
          ]
        },
        {
          title: 'Encuadernado',
          subTitle: 'Espiral y tapas',
          size: '13',
          image: 'assets/images/finishings/encuadernado.svg',
          value: Finishing.BINDING,
          extraData: { isBindingWithCoverLaminated: false },
          settingCategory: SettingCategory.BINDING,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [3],
              disabledTooltip: 'Tamaño de papel incompatible'
            },
            {
              id: 'paperWeight',
              options: [7],
              disabledTooltip: 'Gramaje incompatible'
            }
          ]
        },
        {
          title: 'Encuadernado con portada plastificada',
          subTitle: 'En espiral y con portada plastificada',
          size: '12',
          image: 'assets/images/finishings/encuadernadoPortadaPlastificada.svg',
          value: Finishing.BINDING,
          extraData: { isBindingWithCoverLaminated: true },
          settingCategory: SettingCategory.BINDING,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [3],
              disabledTooltip: 'Tamaño de papel incompatible'
            },
            {
              id: 'paperWeight',
              options: [7],
              disabledTooltip: 'Gramaje incompatible'
            }
          ]
        },
        {
          title: 'Todo plastificado y encuadernado',
          subTitle: 'Todas las hojas plastificadas y encuadernadas',
          size: '13',
          image: 'assets/images/finishings/encuadernadoPlastificado.svg',
          value: Finishing.BINDING_LAMINATED,
          settingCategory: SettingCategory.BINDING,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [3],
              disabledTooltip: 'Tamaño de papel incompatible'
            },
            {
              id: 'paperWeight',
              options: [7],
              disabledTooltip: 'Gramaje incompatible'
            }
          ]
        },
        {
          title: 'Perforado 2',
          subTitle: '2 agujeros',
          size: '6',
          sizeMobile: '13',
          image: 'assets/images/finishings/dosAgujeros.svg',
          value: Finishing.PERFORATED2,
          settingCategory: SettingCategory.OTHERS,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [1, 3],
              disabledTooltip: 'Tamaño de papel incompatible'
            },
            {
              id: 'pageTurn',
              options: [2],
              disabledTooltip: 'Solo borde largo'
            },
          ]
        },
        {
          title: 'Perforado 4',
          subTitle: '4 agujeros',
          size: '6',
          sizeMobile: '13',
          image: 'assets/images/finishings/cuatroAgujeros.svg',
          value: Finishing.PERFORATED4,
          settingCategory: SettingCategory.OTHERS,
          unavailableFor: [
            {
              id: 'pageSize',
              options: [1, 3],
              disabledTooltip: 'Tamaño de papel incompatible'
            },
            {
              id: 'pageTurn',
              options: [2],
              disabledTooltip: 'Solo borde largo'
            },
          ]
        },
        {
          title: 'Plastificado',
          subTitle: 'Hoja plastificada',
          size: '13',
          image: 'assets/images/finishings/plastificado.svg',
          value: Finishing.LAMINATED,
          settingCategory: SettingCategory.SPECIAL,
          unavailableFor: [
            {
              id: 'paperWeight',
              options: [],
              disabledTooltip: 'Gramaje incompatible'
            }
          ]
        },
        // {
        //   title: 'TALADRADO',
        //   subTitle: 'para encuadernación',
        //   size: '6',
        //   value: Finishing.PERFORATEDBIND,
        //   unavailableFor: [
        //     {
        //       id: 'paperWeight',
        //       options: [3],
        //       subTitle: 'Gramaje incompatible'
        //     },
        //     {
        //       id: 'pageSize',
        //       options: [3],
        //       subTitle: 'Tamaño de papel incompatible'
        //     }
        //   ]
        // },
      ]
    },
    grouped: {
      title: null,
      selected: 2,
      options: [
        {
          title: 'Individual',
          subTitle: 'Por separado',
          size: '5.8',
          value: false
        },
        {
          title: 'Agrupado',
          subTitle: 'Todo junto',
          size: '5.8',
          value: true
        },
      ]
    },
    coverColor: {
      title: 'Imprimir portadas a color',
      selected: 1,
      options: [
        {
          title: 'Imprimir portadas a color',
          value: false
        },
        {
          title: 'Imprimir portadas a color',
          value: true
        }
      ]
    },
    coverLaminated: {
      title: 'Portada plastificada a color',
      selected: 1,
      options: [
        {
          title: 'Portada plastificada a color',
          value: false
        },
        {
          title: 'Portada plastificada a color',
          value: true
        }
      ]
    },
    ringColor: {
      title: 'Color de las anillas',
      selected: 1,
      options: [
        // {size:1, value:{name:'Transparente', color: '#FFFFFF'}},
        // {size:1, value:{name:'Negro', color: '#000000'}},
        // {size:1, value:{name:'Turquesa', color: '#40E0D0'}},
        // {size:1, value:{name:'Rosa Pastel', color: '#FFC0CB'}},
        // {size:1, value:{name:'Lila', color: '#9370DB'}},
        // {size:1, value:{name:'Azul Pastel', color: '#87CEFA'}},
        // {size:1, value:{name:'Naranja', color: '#FFA500'}},
        // {size:1, value:{name:'Amarillo', color: '#FFFF00'}}
      ]
    },
    hardCoverFront: {
      title: 'Tapa delantera',
      selected: 1,
      options: []
    },
    hardCoverBack: {
      title: 'Tapa trasera',
      selected: 2,
      options: []
    },
    laminationType: {
      title: 'Tipo de plastificado',
      selected: 1,
      options: [
        {
          title: 'Mate',
          value: LaminationType.Matte,
          size: 6,
          video: "/assets/videos/plastificado-mate.mp4",
          unavailableFor: []
        },
        {
          title: 'Brillo',
          value: LaminationType.Gloss,
          size: 6,
          video: "/assets/videos/plastificado-brillo.mp4",
          unavailableFor: []
        },
      ]
    },
    laminationWeight: {
      title: 'Micraje del plastificado',
      selected: 1,
      options: [
        {
          title: '80',
          value: LaminationWeight.W80,
          size: 6,
          unavailableFor: [
            {
              id: 'laminationType',
              options: [1],
            },
            {
              id: 'finishing',
              options: [4, 5],
            }
          ]
        },
        {
          title: '125',
          value: LaminationWeight.W125,
          size: 6,
          unavailableForGrouped: [
            {
              properties: [
                {
                  id: 'finishing',
                  options: [4, 5]
                },
                {
                  id: 'laminationType',
                  options: [1],
                }
              ],
            }
          ]
        },
        {
          title: '150',
          value: LaminationWeight.W150,
          size: 6,
          unavailableFor: [
            {
              id: 'laminationType',
              options: [2],
            }
          ]
        },
        {
          title: '175',
          value: LaminationWeight.W175,
          size: 6,
          unavailableFor: [
            {
              id: 'laminationType',
              options: [1],
            },
            {
              id: 'finishing',
              options: [4, 5],
            }
          ]
        },
        {
          title: '250',
          value: LaminationWeight.W250,
          size: 6
        },
      ]
    },
    docColor: {
      title: 'Imprimir todo a color',
      selected: 1,
      options: [
        {
          title: 'Imprimir todo a color',
          value: false
        },
        {
          title: 'Imprimir todo a color',
          value: true
        }
      ]
    }
  };

  private printPresets = [
    {
      name: 'Por defecto',
      default: true
    },
    {
      name: 'Preset 1',
      autoRotate: 2,
      twoSided: 1
    },
    {
      name: 'Apuntes'
    },
    {
      name: 'Diplomas'
    }
  ];

  isLargeScreen: boolean = false;
  tooltips: TooltipDirective[] = [];

  constructor(
    private http: HttpClient,
    public platform: Platform,
    private modalController: ModalController,
    private authService: AuthService
  ) {
    //set default printSettings if not exists
    // if (this._printSettings)
    //   db.collection('settings').doc('printSettings').set(this._printSettings);

    this.setIsMobile(this.platform.width());

    this.platform.resize.subscribe(async () => {
      this.setIsMobile(this.platform.width());
    });

    this.printSettingsLoaded$.pipe(
      filter(loaded => loaded === true),
      take(1)
    ).subscribe(() => {
      // Sin take(1): reacciona tanto a la emisión desde caché de Firestore (que puede
      // llegar sin ringColors/hardCovers) como a la posterior del servidor con los datos
      // reales. initPrintEngine se guarda con un flag porque crea suscripciones internas
      // que no se limpian; las llamadas siguientes solo actualizan rings/covers.
      let engineInitialized = false;
      this.productService.printProductDb$.subscribe(p => {
        const ringColors = (p as any).ringColors?.filter(rc => !rc.hide) ?? [];
        const hardCovers = (p as any).hardCovers?.filter(hc => !hc.hide) ?? [];
        if (!engineInitialized) {
          this.productService.initPrintEngine(p as any);
          engineInitialized = true;
        }
        this.setRingsCoversSettings(ringColors, hardCovers);
      });
    });
  }

  setRingsCoversSettings(ringColors, hardCovers) {
    this._ringColors = [...ringColors];
    //Ajustamos PrintSettings
    this.printSettings.ringColor.options = ringColors.map(ringColor => ({ size: 1, value: { ...ringColor } }));
    this.printSettings.hardCoverFront.options = hardCovers.map(hardCover => ({ size: 1, value: { ...hardCover } }));
    this.printSettings.hardCoverBack.options = hardCovers.map(hardCover => ({ size: 1, value: { ...hardCover } }));
    // Notificamos a los consumidores que ya pueden refrescar su copia local de printSettings.
    this._ringsCoversLoaded.next(true);
  }

  setIsMobile(width) {
    this.isMobile.set(width < 1104);
  }

  getPrintSettings() {
    return cloneDeep(this.printSettings);
  }

  // Guardamos printSettings cargado desde settings/printSettings
  setPrintSettings(printSettings: PrintSettingsConfig) {
    this.printSettings = printSettings;
    this.printSettingsLoaded.next(!!printSettings);
  }

  // Guardamos bankInformation cargado desde settings/bankInformation
  setBankInformation(bankInformation: BankInformationSettings) {
    this.bankInformation = bankInformation;
  }

  setWhatsappPhoneNumber(phoneNumber: string | null) {
    this.whatsappPhoneNumber.set(phoneNumber);
  }

  // Actualizamos general settings y estado de bloqueo de pedidos
  setGeneralSettings(generalSetting: GeneralSettings) {
    this._generalSettings.next(generalSetting);
    this.checkOrdersBlocked(generalSetting.threshold.blocked, generalSetting.ordersBlockedTitle, generalSetting.ordersBlockedBody);
  }

  // Actualizamos banners y marcamos la app como inicializada
  setAppBanners(appBanners: BannersSettings) {
    this.appBanners = appBanners;
    this.isInit.next(true);
  }

  getPrintSettingDefault(setting: Settings) {
    const index = this.printSettings[setting].selected - 1;
    return this.printSettings[setting].options[index].value;
  }

  getPrintPresets() {
    return cloneDeep(this.printPresets);
  }

  getSelectedIndexForOption(setting: Settings, properties: any): number {
    const options = this.printSettings[setting].options;
    const index = options.findIndex(option => {
      return Object.entries(properties).every(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(option[key]) === JSON.stringify(value);
        }
        return option[key] === value;
      });
    });
    return index > -1 ? index + 1 : null;
  }

  async getIpInfo() {
    const headers = await this.authService._getHttpHeaders();
    if (headers) {
      return this._ipInfo.getIpInfo();
    } else {
      return Promise.reject(new Error("fail-invalid currentUser"));
    }
  }

  // getIpInfo(){
  //   // const url = `http://www.ip-api.com/json`;
  //   ///ipinfo/
  //   const url = `https://api64.ipify.org/?format=json`;
  //   return this.http.get(url)
  //   .toPromise()
  //   .then((res:any)=>{
  //     return res;
  //   })
  // }

  /**
   * Funciones de upgrade de products
   */
  upgradeCart(cart: ShoppingCart) {
    if (cart && cart.items) {
      cart.items.map(item => {
        this.upgradeStoreProductSnapshot(item.product);
        this.upgradePrintProduct(item.product);
        return item;
      });
    }
  }

  /**
   * Limpieza retro-compatible de snapshots de carrito antiguos.
   *
   * Antes de la corrección del bug de cuelgue al añadir productos con
   * variaciones, `newProductSnapshot` incluía el array `variants` completo
   * (objetos hidratados) dentro del snapshot persistido en localStorage. Esa
   * estructura, combinada con los listeners por variante, causaba bucles de
   * change-detection y dejaba a la app inutilizable hasta limpiar el storage.
   *
   * El nuevo snapshot ya no incluye `variants`, pero los usuarios que
   * guardaron carritos con la versión anterior siguen teniendo basura en su
   * localStorage. Aquí la eliminamos al cargar, sin perder el resto del
   * carrito.
   */
  upgradeStoreProductSnapshot(product: ProductSnapshot) {
    if (!product) return product;
    // Eliminar `variants` heredado de snapshots anteriores. El campo no debe
    // existir en el snapshot del carrito; se rehidrata bajo demanda vía los
    // listeners de Firestore en CartService.setProductUpdates().
    if ('variants' in (product as any)) {
      delete (product as any).variants;
    }
    return product;
  }

  upgradePrintProduct(product: ProductSnapshot) {
    if (product.type === ProductType.PRINT) {
      product.printingGroup.printSettings.paperWeight = product.printingGroup.printSettings.paperWeight ?? PaperWeight.W80;
      this.upgradeRingColor(product);
      this.upgradeHardCover(product);
      product.printingGroup.files.map(file => {
        delete file['thumbFiles'];
        delete file['tooks'];
        return file;
      });
    }
    return product;
  }

  upgradeRingColor(product: ProductSnapshot) {
    if (product.printingGroup.printSettings.ringColor && !product.printingGroup.printSettings.ringColor.id) {
      product.printingGroup.printSettings.ringColor = this._ringColors.find(ringColor => product.printingGroup.printSettings.ringColor.name === ringColor.name);
    }
    if (!product.printingGroup.printSettings.ringColor) {
      product.printingGroup.printSettings.ringColor = this.getPrintSettingDefault(Settings.RING_COLOR);
    }
  }

  upgradeHardCover(product: ProductSnapshot) {
    //Si no tiene hardCover se le agregan
    if (!product.printingGroup.printSettings.hardCoverFront) {
      product.printingGroup.printSettings.hardCoverFront = this.getPrintSettingDefault(Settings.HARD_COVER_FRONT);
      product.printingGroup.printSettings.hardCoverBack = this.getPrintSettingDefault(Settings.HARD_COVER_BACK);
    }
  }

  private async checkOrdersBlocked(ordersBlocked: boolean, title, body) {
    if (ordersBlocked && !this.isOrdersBlocked) {
      const modal = await this.modalController.create({
        component: OrdersBlockedAlertComponent,
        cssClass: "auto-height ",
        backdropDismiss: false,
      });
      modal.present();
    }
    this.isOrdersBlocked = ordersBlocked;
    this.ordersBlockedTitle = title;
    this.ordersBlockedBody = body;
  }

  registerTooltip(tooltip: TooltipDirective) {
    if (tooltip && !this.tooltips.includes(tooltip)) {
      this.tooltips.push(tooltip);
    }
  }

  unregisterTooltip(tooltip: TooltipDirective) {
    this.tooltips = this.tooltips.filter(t => t !== tooltip);
  }

  closeAllTooltips() {
    this.tooltips.forEach(t => {
      t.destroyTooltip({ fast: true });
    });
  }

}
